// Story 024 — focus-equals-review status planner.
//
// `DefectStatus` from `@aeriosiq/types` now includes `"reviewed"` natively
// (contract 5.2.0+), so the local extension that previously lived here
// is no longer required. This file retains the phase-transition planner
// that drives `useFocusEqualsReviewEffect` — the per-phase prereq +
// target mapping defined in `.vibe/19 §1.2` (meeting 2026-02-12).
import type { DefectStatus } from "@aeriosiq/types";

export const PHASE_FORWARD_TARGET: Record<
  "PRE_SCREENING" | "EXPERT_ANALYSIS",
  "reviewed" | "confirmed"
> = {
  PRE_SCREENING: "reviewed",
  EXPERT_ANALYSIS: "confirmed",
};

export const PHASE_FORWARD_PREREQ: Record<
  "PRE_SCREENING" | "EXPERT_ANALYSIS",
  DefectStatus
> = {
  PRE_SCREENING: "detected",
  EXPERT_ANALYSIS: "reviewed",
};

/**
 * Decide whether a defect should auto-advance when the analyst focuses
 * it. Returns the target status (and the matching review phase) when
 * the transition should fire, or `null` to skip silently.
 */
export function planFocusTransition(args: {
  evaluationState: string | null | undefined;
  defectStatus: DefectStatus;
}): {
  targetStatus: "reviewed" | "confirmed";
  reviewPhase: "PRE_SCREENING" | "EXPERT_ANALYSIS";
} | null {
  const { evaluationState, defectStatus } = args;
  if (
    evaluationState !== "PRE_SCREENING" &&
    evaluationState !== "EXPERT_ANALYSIS"
  ) {
    return null;
  }
  const target = PHASE_FORWARD_TARGET[evaluationState];
  const prereq = PHASE_FORWARD_PREREQ[evaluationState];
  if (defectStatus !== prereq) return null;
  return { targetStatus: target, reviewPhase: evaluationState };
}
